// Layout — yeni-design shell (Phase 2 rebuild).
// Outer chrome ported from yeni-design/src/shell.jsx (Sidebar + Header pattern).
// Adapts to existing data layer: PanelConfig props, useBreakpoint, useLocation
// breadcrumbs, AuthContext, DemoBanner/TrialBanner. DynamicSidebar is reused
// inside the new aside (its Antd icons + nav remain — config-driven nav stays
// owned by DynamicSidebar; only the surrounding shell becomes Tailwind).
import { useEffect, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import dayjs from 'dayjs';
import 'dayjs/locale/tr';
import { Drawer } from 'antd';
import { DynamicSidebar } from './DynamicSidebar';
import { DemoBanner } from './DemoBanner';
import { TrialBanner } from './TrialBanner';
import { Wordmark } from './yeni-design/Wordmark';
import { I } from './yeni-design/icons';
import { useAuth } from '../contexts/AuthContext';
import type { PanelConfig } from '../types/panelConfig';

const IS_DEMO = import.meta.env.VITE_DEMO_MODE === 'true';

dayjs.locale('tr');

interface LayoutProps {
  children: ReactNode;
  config: PanelConfig;
}

function useBreakpoint() {
  const [bp, setBp] = useState<'mobile' | 'tablet' | 'desktop'>(() => {
    if (typeof window === 'undefined') return 'desktop';
    if (window.innerWidth < 768) return 'mobile';
    if (window.innerWidth < 1024) return 'tablet';
    return 'desktop';
  });
  useEffect(() => {
    const f = () => {
      if (window.innerWidth < 768) setBp('mobile');
      else if (window.innerWidth < 1024) setBp('tablet');
      else setBp('desktop');
    };
    window.addEventListener('resize', f);
    return () => window.removeEventListener('resize', f);
  }, []);
  return bp;
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'R';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

interface SidebarBodyProps {
  config: PanelConfig;
  collapsed: boolean;
  mobile: boolean;
  onClose?: () => void;
}

function SidebarBody({ config, collapsed, mobile, onClose }: SidebarBodyProps) {
  const { logout } = useAuth();
  const showLabels = !collapsed || mobile;
  const initials = getInitials(config.firmName);

  return (
    <>
      {/* Logo bar */}
      <div
        className={`h-14 border-b border-border flex items-center shrink-0 ${
          collapsed && !mobile ? 'justify-center px-0' : 'px-4'
        }`}
      >
        {collapsed && !mobile ? (
          <img
            src="/raporgor-logo.png"
            alt="RaporGör"
            width={28}
            height={28}
            className="object-contain"
          />
        ) : (
          <Wordmark size="md" />
        )}
        {mobile && onClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Menüyü kapat"
            className="focus-ring ml-auto p-1.5 rounded-md text-ink-500 hover:bg-ink-100 hover:text-ink-900"
          >
            <I.X size={16} />
          </button>
        )}
      </div>

      {/* Nav body — DynamicSidebar (Antd icons inside, kept as-is) */}
      <div className="flex-1 overflow-y-auto thin-scroll">
        <DynamicSidebar
          reports={config.reports}
          collapsed={collapsed && !mobile}
        />
      </div>

      {/* Footer: user card only */}
      <div
        className={`border-t border-border shrink-0 ${
          collapsed && !mobile ? 'p-2' : 'p-3'
        }`}
      >
        {showLabels ? (
          <button
            type="button"
            onClick={logout}
            className="focus-ring flex items-center gap-2.5 w-full px-1.5 py-1.5 rounded-md hover:bg-ink-100 text-left"
            title="Oturumu kapat"
          >
            <div className="w-8 h-8 rounded-md bg-brand-700 text-white text-[12px] font-semibold flex items-center justify-center shrink-0">
              {initials}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[13px] font-semibold text-ink-900 truncate">
                {config.firmName}
              </div>
              <div className="text-[11px] text-ink-500 truncate">
                Oturumu kapat
              </div>
            </div>
          </button>
        ) : (
          <button
            type="button"
            onClick={logout}
            aria-label="Oturumu kapat"
            className="focus-ring w-9 h-9 mx-auto rounded-md bg-brand-700 text-white text-[12px] font-semibold flex items-center justify-center"
          >
            {initials}
          </button>
        )}
      </div>
    </>
  );
}

export function Layout({ children, config }: LayoutProps) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [currentTime, setCurrentTime] = useState(dayjs());
  const bp = useBreakpoint();
  const location = useLocation();

  useEffect(() => {
    const t = setInterval(() => setCurrentTime(dayjs()), 60_000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (bp === 'tablet') setCollapsed(true);
  }, [bp]);

  useEffect(() => setMobileMenuOpen(false), [location.pathname]);

  const isMobile = bp === 'mobile';

  // Breadcrumb segments → category + report name (or just "Dashboard")
  const breadcrumb = (() => {
    if (location.pathname === '/') return ['Dashboard'];
    const code = location.pathname.replace('/rapor/', '');
    const r = config.reports.find((x) => x.code === code);
    if (!r) return ['Rapor'];
    return [r.category, r.name];
  })();

  const headerTitle = breadcrumb[breadcrumb.length - 1];

  return (
    // h-dvh: iOS Safari adres çubuğu 100vh'yi taşırır; safe-area: standalone PWA'da çentik/home-bar
    <div className="flex flex-col h-dvh bg-page text-ink-900 [padding:env(safe-area-inset-top)_env(safe-area-inset-right)_env(safe-area-inset-bottom)_env(safe-area-inset-left)]">
      {IS_DEMO && <DemoBanner />}
      {IS_DEMO && config.trialInfo && (
        <TrialBanner
          daysRemaining={config.trialInfo.daysRemaining}
          expiresAt={config.trialInfo.expiresAt}
        />
      )}

      <div className="flex flex-1 min-h-0">
        {/* Desktop / tablet sidebar */}
        {!isMobile && (
          <aside
            className={`bg-surface border-r border-border flex flex-col h-full shrink-0 transition-[width] duration-150 ${
              collapsed ? 'w-[64px]' : 'w-[240px]'
            }`}
          >
            <SidebarBody
              config={config}
              collapsed={collapsed}
              mobile={false}
            />
          </aside>
        )}

        {/* Mobile drawer (Antd shell, yeni-design content inside) */}
        {isMobile && (
          <Drawer
            placement="left"
            open={mobileMenuOpen}
            onClose={() => setMobileMenuOpen(false)}
            width="min(300px, 85vw)"
            styles={{
              body: { padding: 0, background: '#ffffff' },
              header: { display: 'none' },
            }}
          >
            <div className="flex flex-col h-full bg-surface">
              <SidebarBody
                config={config}
                collapsed={false}
                mobile
                onClose={() => setMobileMenuOpen(false)}
              />
            </div>
          </Drawer>
        )}

        {/* Main column */}
        <div className="flex flex-col flex-1 min-w-0">
          {/* Header */}
          <header className="h-14 bg-surface border-b border-border flex items-center justify-between gap-3 px-4 lg:px-6 shrink-0">
            <div className="flex items-center gap-3 min-w-0">
              {isMobile ? (
                <button
                  type="button"
                  onClick={() => setMobileMenuOpen(true)}
                  aria-label="Menüyü aç"
                  className="focus-ring -ml-1 p-1.5 rounded-md text-ink-700 hover:bg-ink-100"
                >
                  <I.Menu size={18} />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => setCollapsed((c) => !c)}
                  aria-label={collapsed ? 'Menüyü genişlet' : 'Menüyü daralt'}
                  className="focus-ring -ml-1 p-1.5 rounded-md text-ink-700 hover:bg-ink-100"
                >
                  <I.Menu size={18} />
                </button>
              )}

              {/* Desktop: full breadcrumb */}
              <nav
                className="hidden lg:flex items-center gap-1.5 text-[12px] text-ink-500"
                aria-label="Konum"
              >
                {breadcrumb.map((seg, i) => (
                  <span key={i} className="flex items-center gap-1.5">
                    {i > 0 && (
                      <I.ChevronDown
                        size={10}
                        className="text-ink-300 -rotate-90"
                      />
                    )}
                    <span
                      className={
                        i === breadcrumb.length - 1
                          ? 'text-ink-700 font-medium'
                          : ''
                      }
                    >
                      {seg}
                    </span>
                  </span>
                ))}
              </nav>

              {/* Mobile: just the leaf as title */}
              {isMobile && (
                <h1 className="text-[15px] font-semibold text-ink-900 truncate">
                  {headerTitle}
                </h1>
              )}
            </div>

            <div className="flex items-center gap-1.5 shrink-0">
              <div className="hidden sm:inline-flex items-center gap-1.5 text-[12px] text-ink-500 px-2 h-9 rounded-md bg-ink-50 border border-border">
                <I.Calendar size={12} />
                <span className="tnum">
                  {currentTime.format('D MMMM, HH:mm')}
                </span>
              </div>
            </div>
          </header>

          {/* Content */}
          <main className="flex-1 overflow-y-auto overflow-x-hidden bg-page">
            <div
              className={`mx-auto w-full max-w-[1400px] ${
                isMobile ? 'p-4' : 'p-6'
              }`}
            >
              {children}
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
